import React, { useState, useCallback, useEffect } from "react";
import fp from "lodash/fp";
import { Form, Row, Col } from "react-bootstrap";
import { connect, useDispatch } from "react-redux";
import {
	change,
	Field,
	getFormValues,
	InjectedFormProps,
	reduxForm,
} from "redux-form";

import { Button, CustomInput } from "components";

import { ReactComponent as CancelIcon } from "static/images/cancel.svg";
import { ReactComponent as CheckDone } from "static/images/checked.svg";
import { ReactComponent as EditIcon } from "static/images/pencil.svg";
import styles from "./../autoCheckList.module.scss";

type ExperianFieldFormProps = {
	isLoading: boolean;
	isEditExperianFields: boolean;
	label: string;
	value?: string | number;
	fieldName: string;
	ownerType: string;
	formValues: { [key: string]: any };
	dataFormat?: Function;
	normalize?: Function;
	validate?: any;
	normalizeOnBlur?: Function;
	updateExperianField: (
		formValues: { [key: string]: any },
		ownerType: string,
		hideEditField: Function,
	) => void;
};

const FORM_NAME = "resultChecklistExperianFieldForm";

const ExperianFieldForm = ({
	isLoading,
	label,
	value,
	fieldName,
	formValues,
	dataFormat,
	ownerType,
	validate,
	normalize,
	normalizeOnBlur,
	handleSubmit,
	updateExperianField,
	isEditExperianFields,
}: InjectedFormProps<any, ExperianFieldFormProps> & ExperianFieldFormProps) => {
	const [showEditField, setShowEditField] = useState(false);

	const dispatch = useDispatch();

	const formattedValue = ({ dataFormat, value, isEdit = false }) =>
		dataFormat ? dataFormat(value, isEdit) : value;

	const setInitialValue = useCallback(
		() =>
			dispatch(
				change(
					FORM_NAME.concat(`${ownerType}${fieldName}`),
					fieldName,
					formattedValue({ dataFormat, value, isEdit: true }),
				),
			),
		[dispatch, ownerType, fieldName, value, dataFormat],
	);

	useEffect(() => {
		setInitialValue();
	}, [setInitialValue, value]);

	const handleCancelEdit = () => {
		setShowEditField(!showEditField);
		dispatch(
			change(
				FORM_NAME.concat(`${ownerType}${fieldName}`),
				fieldName,
				formattedValue({ dataFormat, value, isEdit: true }),
			),
		);
	};

	const DataRow = useCallback(
		({ label, value, dataFormat }) => {
			return (
				<Row className={styles.field}>
					<Col className={styles.label}>{`${label}:`}</Col>
					<Col
						className={`${
							!isEditExperianFields && "pr-4"
						} d-flex justify-content-end pr-0`}
					>
						{formattedValue({ dataFormat, value }) ?? ""}
					</Col>
				</Row>
			);
		},
		[isEditExperianFields],
	);

	const handleUpdateExperianField = values => {
		updateExperianField(values, ownerType, () =>
			setShowEditField(!showEditField),
		);
	};

	return (
		<Form
			onSubmit={handleSubmit(values => {
				handleUpdateExperianField(values);
			})}
			className={`${styles.containerExperianFormField} d-flex w-100`}
		>
			{showEditField ? (
				<div className="d-flex w-100">
					<Field
						component={CustomInput}
						smLabel="5"
						smSelect={{ span: 5, offset: 2 }}
						placeholder=""
						type="text"
						validate={validate}
						normalize={normalize}
						normalizeOnBlur={normalizeOnBlur}
						label={`${label}:`}
						name={fieldName}
						blurLoading={isLoading}
						wrapperCustomInput="w-100"
						labelClassName={styles.labelFormField}
						wrapperClassName={styles.experianUpdateInfoField}
					/>
					<div className="d-flex ml-2">
						<Button
							onClick={() => {
								handleCancelEdit();
							}}
							icon={<CancelIcon />}
							btnIcon
							variant="secondary-danger"
							size="sm"
							disabled={isLoading}
						/>
						<Button
							type="submit"
							icon={<CheckDone />}
							btnIcon
							variant="secondary"
							size="sm"
							disabled={
								fp.isEmpty(formValues) ||
								formValues[fieldName]?.toString() ===
									formattedValue({
										dataFormat,
										value,
										isEdit: true,
									}).toString() ||
								isLoading
							}
						/>
					</div>
				</div>
			) : (
				<div className="d-flex w-100">
					<DataRow
						label={label}
						value={value}
						dataFormat={dataFormat}
					/>
					{isEditExperianFields && (
						<>
							{!isLoading && (
								<Button
									onClick={() => {
										setShowEditField(!showEditField);
									}}
									icon={<EditIcon />}
									btnIcon
									className={`ml-4`}
									variant="secondary"
									size="sm"
								/>
							)}
						</>
					)}
				</div>
			)}
		</Form>
	);
};

const mapStateToProps = (state, { fieldName, ownerType }) => ({
	form: FORM_NAME.concat(`${ownerType}${fieldName}`),
	formValues: getFormValues(FORM_NAME.concat(`${ownerType}${fieldName}`))(
		state,
	),
});

const experianFieldForm = fp.flow(
	reduxForm<any, ExperianFieldFormProps>({
		form: FORM_NAME,
		enableReinitialize: true,
		destroyOnUnmount: false,
	}),
	connect(mapStateToProps),
)(ExperianFieldForm);

export default experianFieldForm;
